import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import { Icon, IconType } from 'record-it-components/misc/Icon/Icon';
import {
  CreateEntityButtonConfig,
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { WorkTimeEntryList } from '../../workTime/WorkTimeEntryList/WorkTimeEntryList';
import { useWorkTimeAdminPageFilters } from './useWorkTimeAdminPageFilters';
import { ExportAdminWorkTimeDialog } from '../../workTime/ExportAdminWorkTimeDialog/ExportAdminWorkTimeDialog';

export type WorkTimeAdminPageProps = PropsWithoutChildren<Record<never, never>>;

export const WorkTimeAdminPage: FunctionComponent<WorkTimeAdminPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { getRequiredCurrentUserPermissions } = useCurrentUser();
    const { t } = useTranslation();
    const { userGroupId, userId } = useSpecificParams({
      requiredParamNames: ['userGroupId', 'userId']
    });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addWorkTimeAdminPage(userGroupId, userId)
    );

    const workTimeEntries = useEntities(EntityName.WorkTimeEntry, 'getAll');
    const sortedWorkTimeEntries = useMemo(() => {
      return workTimeEntries.sort((a, b) => b.endDate.localeCompare(a.endDate));
    }, [workTimeEntries]);

    const {
      close: closeExportModal,
      open: openExportModal,
      modalProps: exportModalProps
    } = useModalOpened({ closeOnBackDropClick: true });

    const createEntityButtonConfig: CreateEntityButtonConfig<unknown> = useMemo(
      () => ({
        visible: false
      }),
      []
    );

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page.WorkTimeAdminPage.titleIcon,
        title: t('components.page.WorkTimeAdminPage.pageTitle'),
        // eslint-disable-next-line react/no-multi-comp
        renderAdditionalRightContent: () => [
          <ButtonStandard
            key="WorkTimeAdminPage.ExportWorkTimeButton"
            onClick={openExportModal}
            data-testid="WorkTimeAdminPage.ExportWorkTimeButton"
          >
            <Icon
              type={IconType.Regular}
              name="file-export"
            ></Icon>
          </ButtonStandard>
        ]
      };
    }, [t, openExportModal]);

    const filters = useWorkTimeAdminPageFilters({
      userGroupId,
      userId,
      getRequiredCurrentUserPermissions
    });

    const renderEntities = useCallback(
      (currentPageWorkTimeEntries: Array<WorkTimeEntry>) => {
        return (
          <WorkTimeEntryList
            workTimeEntries={currentPageWorkTimeEntries}
          ></WorkTimeEntryList>
        );
      },
      []
    );

    return (
      <>
        <ManageEntitiesContentLayout<WorkTimeEntry, unknown>
          headerConfig={headerConfig}
          createEntityButtonConfig={createEntityButtonConfig}
          entities={sortedWorkTimeEntries}
          filters={filters}
          renderEntities={renderEntities}
        ></ManageEntitiesContentLayout>

        <ExportAdminWorkTimeDialog
          {...exportModalProps}
          onCancelClick={closeExportModal}
          data-testid="WorkTimeAdminPage.ExportWorkTimeDialog"
        ></ExportAdminWorkTimeDialog>
      </>
    );
  });
